import { useEffect, useMemo, useState } from "react";
import { ArrowDown2, SearchNormal } from "iconsax-reactjs";
import { X } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";

type AssignableMember = {
  id: string;
  name: string;
  label: string;
};

type AssignMembersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  members: AssignableMember[];
  selectedMemberIds: string[];
  memberQuery: string;
  onMemberQueryChange: (query: string) => void;
  onToggleMember: (memberId: string) => void;
  onAssign: () => void;
  onCancel: () => void;
  isAssigning: boolean;
  error?: string | null;
};

const AssignMembersModal = ({
  open,
  onOpenChange,
  roleName,
  members,
  selectedMemberIds,
  memberQuery,
  onMemberQueryChange,
  onToggleMember,
  onAssign,
  onCancel,
  isAssigning,
  error,
}: AssignMembersModalProps) => {
  const [isMembersDropdownOpen, setIsMembersDropdownOpen] = useState(false);

  useEffect(() => {
    if (!open) setIsMembersDropdownOpen(false);
  }, [open]);

  const selectedMemberNames = useMemo(() => {
    const selectedSet = new Set(selectedMemberIds);
    return members
      .filter((member) => selectedSet.has(member.id))
      .map((member) => member.name);
  }, [members, selectedMemberIds]);

  const filteredMembers = useMemo(() => {
    const normalized = memberQuery.trim().toLowerCase();
    if (!normalized) return members;
    return members.filter((member) =>
      member.name.toLowerCase().includes(normalized),
    );
  }, [memberQuery, members]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-[600px]"
    >
      <ModalHeader>
        <ModalTitle>Assign role</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={onCancel}
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="space-y-6 pt-4">
        <div>
          <div className="mb-2 text-[14px] font-[450] text-[#2C2F44]">
            Member
          </div>
          <button
            type="button"
            onClick={() => setIsMembersDropdownOpen((prev) => !prev)}
            className="flex py-4 w-full items-center justify-between rounded-[16px] bg-[#E9ECFA] px-5 text-left"
          >
            <span className="truncate text-[16px] font-[400] text-[#737AA8]">
              {selectedMemberNames.length > 0
                ? selectedMemberNames.join(", ")
                : "Select members"}
            </span>
            <ArrowDown2
              size={20}
              className={`text-[#737AA8] transition-transform ${
                isMembersDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>

        {isMembersDropdownOpen ? (
          <div className="rounded-[18px] bg-white p-4">
            <div className="mb-4 text-[14px] font-[450] text-[#2C2F44]">
              Members
            </div>
            <Input
              value={memberQuery}
              onChange={(event) => onMemberQueryChange(event.target.value)}
              placeholder="Search members"
              startIcon={<SearchNormal size={20} className="text-[#737AA8]" />}
              className="h-14 rounded-[14px] bg-[#E9ECFA] text-[16px]"
            />

            <div className="mt-4 max-h-[280px] space-y-2 overflow-y-auto pr-1">
              {filteredMembers.length === 0 ? (
                <div className="rounded-[12px] px-3 py-4 text-[14px] text-[#6B6F8A]">
                  No members found.
                </div>
              ) : (
                filteredMembers.map((member) => {
                  const isSelected = selectedMemberIds.includes(member.id);

                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => onToggleMember(member.id)}
                      className="flex w-full items-center justify-between rounded-[12px] px-3 py-2 text-left transition-colors hover:bg-[#F5F7FF]"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="size-10 bg-[#EEF1FB]">
                          <AvatarFallback className="bg-[#EEF1FB] text-[16px] font-[500] text-[#3B4060]">
                            {member.label}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-[18px] font-[450] text-[#161827]">
                          {member.name}
                        </span>
                      </div>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onToggleMember(member.id)}
                      />
                    </button>
                  );
                })
              )}
            </div>

            {error ? (
              <div className="mt-4 text-[14px] text-[#C24E4E]">{error}</div>
            ) : null}
          </div>
        ) : null}

        <div className="text-[14px] text-[#6B6F8A]">
          Selected role:{" "}
          <span className="font-[500] text-[#2C2F44]">{roleName}</span>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="ghost"
          className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
          onClick={onCancel}
          disabled={isAssigning}
        >
          Cancel
        </Button>
        <Button
          className="h-11 rounded-[14px] px-6"
          onClick={onAssign}
          disabled={selectedMemberIds.length === 0 || isAssigning}
        >
          {isAssigning ? "Assigning..." : "Assign"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export type { AssignableMember };
export default AssignMembersModal;
